<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t(tKey + '.location.title')"
            :description="$t(tKey + '.location.description')"
          >
            <div class="flex flex-col gap-y-5">
              <MultiSwitch
                v-model="rawMounting.mountingPlace"
                :options="[
                  {
                    value: 'location',
                    name: t('mounting.location'),
                  },
                  {
                    value: 'home',
                    name: t('mounting.home'),
                  },
                ]"
                :label="$t('navigation.mountings.DEFAULT')"
                class="mb-10"
              />
              <template v-if="rawMounting.mountingPlace === 'location'">
                <PinMap
                  v-model="rawMounting.gisLocation"
                  class="mb-5"
                />
                <Multiselect
                  v-model="rawMounting.region"
                  :error="errors?.region"
                  :options="fetchRegions"
                  :label="$t('mounting.region')"
                  class="w-full"
                  :can-deselect="false"
                  :can-clear="false"
                  :searchable="true"
                />
                <Multiselect
                  ref="addressSelect"
                  v-model="rawMounting.address"
                  :create-option="true"
                  :error="errors?.address"
                  :options="fetchGoogleLocations"
                  :min-chars="2"
                  :delay="0"
                  :filter-results="false"
                  :searchable="true"
                  :clear-on-select="true"
                  :label="$t('mounting.address')"
                  :placeholder="$t('mounting.address')"
                />
              </template>
              <Switch
                v-model="rawMounting.archived"
                :error="errors?.archived"
                :label="$t('mounting.archived')"
              />
            </div>
          </form-card>
          <form-card
            :title="$t(tKey + '.forBusiness.title')"
            :description="$t(tKey + '.forBusiness.description')"
          >
            <div class="flex flex-col gap-y-5">
              <Switch
                v-model="rawMounting.forBusinessOnly"
                :label="$t('mounting.forBusinessOnly')"
              />

              <Multiselect
                v-if="rawMounting.forBusinessOnly"
                v-model="rawMounting.company"
                :error="errors?.company"
                :options="fetchCompanies"
                :label="$t('mounting.company')"
                class="w-full"
                :can-deselect="true"
                :can-clear="true"
                :searchable="true"
              />
              <Multiselect
                v-if="rawMounting.company"
                ref="licenseSelect"
                v-model="rawMounting.license"
                :error="errors?.license"
                :options="fetchLicenses"
                :label="$t('mounting.license')"
                class="w-full"
                :searchable="false"
                :filter-results="false"
                :clear-on-select="false"
                :can-deselect="true"
                :can-clear="true"
              />
            </div>
          </form-card>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, inject, nextTick, ref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { boolean, object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import type { ProviderParams } from '@utils/table/hooks/provider';
import { syncRef } from '@hooks/state';
import { format } from 'date-fns';
import CustomButton from '@utils/CustomButton.vue';
import PinMap from '@components/utils/maps/PinMap.vue';
import Switch from '@utils/inputs/Switch.vue';
import MultiSwitch from '@utils/inputs/MultiSwitch.vue';
import type {
  Company, License, Location, Mounting, Region,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  mounting?: Mounting;
}

const emit = defineEmits(['update:open', 'update:mounting', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalMounting = useVModel(props, 'mounting', emit);

const isNew = computed(() => !props.mounting);
const tKey = computed(() =>
  (isNew.value ? 'pages.mountings.mountingList.modal.new' : 'pages.mountings.mountingList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const addressSelect = ref();

const fetchRegions = collOptionsProvider<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
  }),
);

const initialData = (mounting?: Mounting) =>
  (mounting
    ? {
      ...mounting,
      mountingPlace: !mounting.region ? 'home' : 'location',
    }
    : {
      region: undefined,
      address: undefined,
      gisLocation: undefined,
      archived: false,
      mountingPlace: 'location',
      forBusinessOnly: false,
      company: undefined,
      license: undefined,
    });

const rawMounting = syncRef(internalMounting, (mounting) => ({
  ...initialData(mounting),
}));

const fetchCompanies = collOptionsProvider<Company>(
  '/companies',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
  }),
);

const licenseSelect = ref<any>(null);
watch(() => rawMounting.value.company, () => {
  licenseSelect.value?.refreshOptions();
});

const fetchLicenses = () => {
  const filterQuery: ProviderParams = {
    filter: { company: typeof rawMounting.value.company === 'string' ? rawMounting.value.company : '' },
  };
  return collOptionsProvider<License>('/licenses', filterQuery, (value) => ({
    // eslint-disable-next-line max-len
    label: `${value.company?.name} (${value.startDate ? format(new Date(value.startDate), 'dd.MM.yyyy') : ''} - ${value.endDate ? format(new Date(value.endDate), 'dd.MM.yyyy') : ''})`,
    value: value['@id'],
  }))();
};

const fetchGoogleLocations = async (query: string, select$) => {
  const input = query || select$.modelValue;
  if (!input) return [];

  const autocompleteService = new google.maps.places.AutocompleteService();

  const response = await autocompleteService.getPlacePredictions({
    input,
  });
  const results = response.predictions.map((prediction) => prediction.description);
  if (!results.length) {
    return [input];
  }
  if (!results.includes(query)) {
    return results;
  }

  return results;
};

const setupOnGmapLoaded = () => {
  const geocoder = new google.maps.Geocoder();
  let lastFoundLocation: Location | undefined;
  let lastFoundAddress: string | undefined;
  const areEqualLocations = (a: Location, b: Location) => a.lat === b.lat && a.long === b.long;

  watch(() => rawMounting.value.gisLocation, async (newLocation) => {
    if (newLocation && !(lastFoundLocation && areEqualLocations(lastFoundLocation, newLocation))) {
      const geocodeResult = await geocoder.geocode({
        location: {
          lat: newLocation?.lat,
          lng: newLocation?.long,
        },
      });
      const address = geocodeResult.results[0]?.formatted_address;

      lastFoundAddress = address;

      rawMounting.value.address = address;
      await nextTick();
      addressSelect.value?.refreshOptions(async () => {
        await nextTick();
        addressSelect.value?.select(address);
      });
    }
  });

  watch(() => rawMounting.value.address, async (newAddress) => {
    if (newAddress?.length && !(lastFoundAddress && newAddress === lastFoundAddress)) {
      const geocodeResult = await geocoder.geocode({
        address: newAddress,
      });

      const latLng = geocodeResult.results[0]?.geometry.location;
      const location = {
        lat: latLng.lat(),
        long: latLng.lng(),
      };

      lastFoundLocation = location;

      rawMounting.value.gisLocation = location;
    }
  });
};

const gmapApiPromiseLazy = inject('gmapApiPromiseLazy') as () => Promise<unknown>;
gmapApiPromiseLazy().then(setupOnGmapLoaded);

const {
  data: mounting,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    region: string().nullable().when('mountingPlace', {
      is: 'location',
      then: (schema) => schema.required(t('validation.notEmpty')),
    }),
    address: string().nullable().when('mountingPlace', {
      is: 'location',
      then: (schema) => schema.required(t('validation.notEmpty')),
    }),
    gisLocation: object().nullable(),
    archived: boolean(),
  }),
  rawMounting,
);

function resetData() {
  rawMounting.value = { ...initialData() } as unknown as typeof mounting['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalMounting.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.mounting;
    const url = edit ? props.mounting['@id'] : '/mountings';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        region: mounting.value.mountingPlace === 'location' ? mounting.value.region : null,
        address: mounting.value.mountingPlace === 'location' ? mounting.value.address : '',
        gisLocation: mounting.value.mountingPlace === 'location' ? mounting.value.gisLocation : null,
        archived: mounting.value.archived,
        forBusinessOnly: mounting.value.forBusinessOnly ?? false,
        company: mounting.value.company ?? null,
        license: mounting.value.license ?? null,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
